import {announce} from '@github-ui/aria-live'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {CreateSavedViewResponse, UpdateSavedViewVariables} from '../components/views/types'
import {LABELS} from '../constants/labels'
import {parseFetchErrorMessage, SAVED_VIEW_QUERY_KEY, SAVED_VIEWS_QUERY_KEY} from '../queries/saved-views'

interface UseUpdateSavedViewMutationOptions {
  owner: string
  repo: string
}

export function useUpdateSavedViewMutation({owner, repo}: UseUpdateSavedViewMutationOptions) {
  const queryClient = useQueryClient()

  return useMutation<CreateSavedViewResponse, Error, UpdateSavedViewVariables>({
    mutationFn: async ({id, ...body}) => {
      const response = await verifiedFetchJSON(`/${owner}/${repo}/issues/views/${id}.json`, {
        method: 'PUT',
        body,
      })
      if (!response.ok) {
        throw new Error(await parseFetchErrorMessage(response, LABELS.views.updateError))
      }
      return (await response.json()) as CreateSavedViewResponse
    },
    onSuccess: ({savedView}) => {
      queryClient.setQueryData([SAVED_VIEW_QUERY_KEY, owner, repo, String(savedView.id)] as const, {savedView})
      void queryClient.invalidateQueries({queryKey: [SAVED_VIEWS_QUERY_KEY, owner, repo]})
    },
    onError: () => announce(LABELS.views.updateError, {assertive: true}),
  })
}
